import { useId, useMemo } from "react";

import { useStableRest } from "../../utils/use-stable-rest";
import type {
  RadioGroupRootAttributes,
  RadioGroupViewModel,
  UseRadioGroupOptions,
} from "./types-radio-group";

const isTrue = (value: boolean | "true" | "false" | undefined) =>
  value === true || value === "true";

/** ViewModel do RadioGroup.Root: `name`, estado que os itens leem pelo contexto e props de acessibilidade da raiz; sem JSX. */
export function useRadioGroup<T>({
  name,
  value,
  onChange,
  orientation = "vertical",
  disabled = false,
  invalid = false,
  testId,
  className,
  style,
  classNames,
  id,
  "aria-labelledby": ariaLabelledBy,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
  "aria-required": ariaRequired,
  ...props
}: UseRadioGroupOptions<T> & RadioGroupRootAttributes<T>): RadioGroupViewModel {
  const rest = useStableRest(props);
  const generatedName = useId();
  const groupName = name ?? generatedName;
  const isInvalid = invalid || isTrue(ariaInvalid);

  return useMemo(
    () => ({
      orientation,
      testId,
      className,
      style,
      classNames,
      rest,
      rootProps: {
        id,
        role: "radiogroup" as const,
        "aria-labelledby": ariaLabelledBy,
        "aria-describedby": ariaDescribedBy,
        "aria-invalid": isInvalid || undefined,
        "aria-required": ariaRequired,
        "aria-disabled": disabled || undefined,
      },
      name: groupName,
      value,
      select: (next: unknown) => {
        if (!disabled) onChange(next as T);
      },
      disabled,
      invalid: isInvalid,
    }),
    [
      orientation,
      testId,
      className,
      style,
      classNames,
      rest,
      id,
      ariaLabelledBy,
      ariaDescribedBy,
      isInvalid,
      ariaRequired,
      groupName,
      value,
      disabled,
      onChange,
    ],
  );
}
